/**
 * Utility functions for matching and identifying locations
 * Handles ID normalization and coordinate-based matching
 */

/**
 * Get location ID from a location object
 * Normalizes different ID field names (_id, location_id) to a single value
 *
 */
export const getLocationId = (location) => {
	return location?._id || location?.location_id || null;
};

/**
 * Round a coordinate to 2 decimal places for consistent matching
 * Used to handle floating point precision issues when comparing coordinates
 *
 */
export const roundCoordinate = (coordinate) => {
	return Math.round(coordinate * 100) / 100;
};

/**
 * Check if two locations match by ID or coordinates
 * Tries ID matching first, falls back to coordinate matching
 *
 */
export const locationsMatch = (location1, location2) => {
	if (!location1 || !location2) return false;

	// Try matching by ID first
	const id1 = getLocationId(location1);
	const id2 = getLocationId(location2);

	if (id1 && id2 && id1 === id2) {
		return true;
	}

	// Fall back to coordinate matching
	if (location1.lat && location1.lng && location2.lat && location2.lng) {
		const lat1Rounded = roundCoordinate(location1.lat);
		const lng1Rounded = roundCoordinate(location1.lng);
		const lat2Rounded = roundCoordinate(location2.lat);
		const lng2Rounded = roundCoordinate(location2.lng);

		return lat1Rounded === lat2Rounded && lng1Rounded === lng2Rounded;
	}

	return false;
};

/**
 * Find a location in an array by coordinates
 * Searches by ID first, then by rounded coordinate matching
 *
 */
export const findLocationByCoordinates = (locations, targetLocation) => {
	if (!locations || !targetLocation) return undefined;

	// Try ID match first
	const targetId = getLocationId(targetLocation);
	if (targetId) {
		const idMatch = locations.find((loc) => getLocationId(loc) === targetId);
		if (idMatch) return idMatch;
	}

	// Fall back to coordinate matching
	if (targetLocation.lat && targetLocation.lng) {
		const targetLatRounded = roundCoordinate(targetLocation.lat);
		const targetLngRounded = roundCoordinate(targetLocation.lng);

		return locations.find((loc) => {
			if (!loc.lat || !loc.lng) return false;

			const locLatRounded = roundCoordinate(loc.lat);
			const locLngRounded = roundCoordinate(loc.lng);

			return locLatRounded === targetLatRounded && locLngRounded === targetLngRounded;
		});
	}

	return undefined;
};

/**
 * Check if a location has already been added to a list
 * Useful for determining button states (disable "Add" if already added)
 *
 */
export const isLocationAdded = (location, addedLocations) => {
	if (!location || !addedLocations || addedLocations.length === 0) {
		return false;
	}

	return addedLocations.some((addedLoc) => locationsMatch(location, addedLoc));
};
